import React, { useMemo } from 'react'
import CreatableSelect from 'react-select/creatable'
import { components, GroupBase, MenuProps, MultiValue } from 'react-select'
import { useTypedTranslation } from 'app/modules/shared/hooks/use-translation'
import { getThemeColor } from 'app/modules/shared/helpers/theme'
import { useTheme } from 'app/modules/shared/context/theme-context'

type OptionType = {
  label: string
  value: string
}

function getMenu(addPrefix?: string) {
  return (props: MenuProps<OptionType, true>) => {
    const { options, selectProps } = props
    let isNew = false
    for (const opt of options as OptionType[]) {
      if (opt.value === selectProps.inputValue && opt.label === `Create "${opt.value}"`) {
        isNew = true
        break
      }
    }

    if (!selectProps.inputValue || !isNew) return null

    const handleChange = () => {
      const values = selectProps.value ?? []
      if (Array.isArray(values)) {
        selectProps.onChange?.([...values, { label: selectProps.inputValue, value: selectProps.inputValue }], {
          action: 'create-option',
          option: {} as OptionType,
        })
        selectProps.onInputChange?.('', { action: 'input-change', prevInputValue: selectProps.inputValue })
        selectProps.onMenuClose?.()
      }
    }

    return (
      <components.Menu<OptionType, true, GroupBase<OptionType>> {...props} selectProps={selectProps}>
        {isNew ? (
          <p className="px-2 py-3 m-0 general-colors" role="button" onClick={handleChange}>
            {`${addPrefix ? addPrefix + ' ' : ''}"${selectProps.inputValue}"`}
          </p>
        ) : (
          props.children
        )}
      </components.Menu>
    )
  }
}

interface MultiInputProps {
  inputClass?: string
  labelClass?: string
  value?: string
  options?: { label: string; value: string }[]
  allowCreate?: boolean
  showList?: boolean
  onChange: (value: string) => void
  placeholder?: string
  addPrefix?: string
  noOptionsMessage?: string
}
export const MultiInput = ({
  inputClass,
  value,
  onChange,
  options = [],
  allowCreate = true,
  showList = false,
  placeholder,
  addPrefix = 'Add item',
  noOptionsMessage = 'no-tags',
}: MultiInputProps): JSX.Element => {
  return (
    <div className={inputClass}>
      <MultiSelect {...{ value, onChange, options, allowCreate, showList, placeholder, addPrefix, noOptionsMessage }} />
    </div>
  )
}

type MultiSelectProps = Readonly<{
  value?: string
  onChange: (value: string) => void
  options: { label: string; value: string }[]
  allowCreate: boolean
  showList: boolean
  placeholder?: string
  addPrefix?: string
  noOptionsMessage: string
}>
function MultiSelect({
  value,
  onChange,
  options,
  allowCreate,
  showList,
  placeholder,
  addPrefix,
  noOptionsMessage,
}: MultiSelectProps) {
  const { _t } = useTypedTranslation()
  const { mode } = useTheme()

  const bgColor = getThemeColor(mode, 'color-background')
  const pmColor = getThemeColor(mode, 'color-primary')
  const txtColor = getThemeColor(mode, 'color-text')
  const components = showList ? {} : { Menu: getMenu(addPrefix), DropdownIndicator: null }
  // Undefined simply falls back to 'Create...'
  const createLabel = showList ? (input: string) => `${addPrefix} "${input}"` : undefined
  const tags = value?.split(',') ?? []
  const arrayValue = useMemo(
    () => tags.filter((value) => !!value.length).map((value) => ({ label: value, value })),
    [value]
  )

  const handleChange = (values: MultiValue<{ value: string; label: string }>, _: any) => {
    const uniqueValues = [...new Set(values.map(({ value }) => value.trim()).filter((value) => !!value.length))]
    onChange(uniqueValues.join(','))
  }

  return (
    <CreatableSelect
      className="p-0 form-control text-main h-auto"
      isMulti
      menuPortalTarget={document.body}
      menuPosition="fixed"
      loadingMessage={({ inputValue }) => `${addPrefix ? addPrefix + ' ' : ''}${inputValue}`}
      options={options}
      placeholder={placeholder ? _t(placeholder) : _t('select-tags')}
      noOptionsMessage={() => (value ? null : _t(noOptionsMessage))}
      formatCreateLabel={createLabel}
      value={arrayValue}
      onChange={handleChange}
      isValidNewOption={(inputValue) => allowCreate && !!inputValue.trim().length}
      components={components}
      classNamePrefix="multi-select"
      styles={{
        control: (provided, state) => {
          const baseStyle = {
            minHeight: '34px',
            height: 'auto',
            color: txtColor,
            backgroundColor: bgColor,
            borderWidth: '1px',
            borderRadius: 0,
            borderColor: getThemeColor(mode, 'color-border'),
            boxShadow: 'none',
            cursor: 'text',
            ':hover': {
              borderColor: pmColor,
            },
          }

          const focusedStyle = state.isFocused ? { borderColor: pmColor } : {}

          return {
            ...provided,
            ...baseStyle,
            ...focusedStyle,
          }
        },
        container: (provided) => ({
          ...provided,
          minHeight: '34px',
          padding: 0,
          border: 'none !important',
          boxShadow: 'none',
        }),
        indicatorSeparator: (provided) => ({
          ...provided,
          display: 'none',
        }),
        input: (provided) => ({
          ...provided,
          color: txtColor,
          lineHeight: '20px',
          backgroundColor: bgColor,
          margin: 0,
        }),
        clearIndicator: (provided) => ({
          ...provided,
          content: 'url(assets/icons/opswat/close.svg)',
          color: getThemeColor(mode, 'color-text-muted'),
          ':hover': {
            color: getThemeColor(mode, 'color-text'),
            cursor: 'pointer',
          },
          fontSize: '16px',
          padding: '8px',
          lineHeight: '20px',
          '& > svg': {
            height: '16px',
            width: '16px',
          },
        }),
        menu: (provided, _) => ({
          ...provided,
          width: '100%',
          minWidth: provided.width,
          maxWidth: 560,
          color: txtColor,
          background: bgColor,
          border: '1px solid ' + getThemeColor(mode, 'color-border-extra-light'),
          boxShadow: '0px 2px 8px ' + getThemeColor(mode, 'color-shadow'),
          borderRadius: 0,
        }),
        menuPortal: (base) => ({ ...base, zIndex: 1000 }),
        valueContainer: (provided) => ({
          ...provided,
          padding: '4px 8px',
          boxShadow: 'none',
          display: 'flex',
          gap: '4px',
        }),
        placeholder: (provided, state) => ({
          ...provided,
          ...(state.isFocused ? { display: 'none' } : {}),
        }),
        multiValue: (provided) => ({
          ...provided,
          color: getThemeColor(mode, 'color-text-muted'),
          borderRadius: 0,
          backgroundColor: getThemeColor(mode, 'color-background-selected'),
          ':hover': {
            backgroundColor: getThemeColor(mode, 'color-hover-background'),
            color: txtColor,
          },
          display: 'flex',
          gap: '8px',
          margin: 0,
        }),
        multiValueLabel: (provided) => ({
          ...provided,
          borderRadius: 0,
          lineHeight: '20px',
          fontSize: '13px',
          fontWeight: '400',
          fontFamily: 'Roboto',
          padding: '2px 0',
          paddingLeft: '8px',
        }),
        multiValueRemove: (provided) => ({
          ...provided,
          content: 'url(assets/icons/opswat/close.svg)',
          cursor: 'pointer',
          color: getThemeColor(mode, 'color-text-muted'),
          ':hover': {
            backgroundColor: getThemeColor(mode, 'color-hover-background'),
            color: txtColor,
          },
          fontSize: '16px',
          padding: '4px',
        }),
      }}
    />
  )
}
